// Aintree — upcoming SIL home (Melbourne's growth corridor).
//
// Copy discipline: Reframed 2026-02 to remove luxury real-estate wording
// ("premium", "spa", "marble", "hotel-grade", "walk-in robe",
// "cathedral raked roofline", "freestanding deep bathtub").
// This is an NDIS SIL home — copy centres accessibility, calm, dignity.

export const AINTREE = {
  slug: "aintree-house",
  id: "prop-aintree-house",
  href: "/homes/aintree-house",
  name: "The Aintree House",
  suburb: "Aintree",
  city: "melbourne",
  state: "VIC",
  postcode: "3336",
  region: "Melbourne's western growth corridor",
  country: "AU",
  lat: -37.6982,
  lon: 144.6710,
  short: "SIL Home · Aintree, VIC · Coming soon",

  published: true,
  status: "upcoming", // "available" | "upcoming" | "filled"
  listing_status: "upcoming",
  recordVerificationStatus: "verified",
  vacancy_available: true,
  vacancies: 3,
  total_bedrooms: 4,
  participant_bedrooms: 3,
  occupied_bedrooms: 0,
  ooa_room: true,
  gender_preference: null,
  preferred_participant_profile: "Adults transitioning into a brand-new accessible home with 1:1 or 1:2 support.",
  available_from: "2026-04-01",
  vacancy_last_updated: "2026-02-15",

  support_ratios: ["1:1", "1:2"],
  support_model: ["24-7", "sleepover", "individualised", "shared"],
  sda_category: null,
  accessibility_features: [
    "step-free-entry", "wide-doorways", "accessible-bathroom", "secure-outdoor",
  ],
  suitability_tags: [
    "psychosocial-disability", "physical-disability", "intellectual-disability", "community-transition",
  ],

  property_type: "house",
  bathrooms: 3,
  living_areas: 2,
  outdoor_area: true,
  secure_yard: true,
  parking: 2,
  garage: true,
  nearby: {
    transport: "Melton line · V/Line network",
    shops: "Woodlea town centre",
    medical: "Local GP · Melton Health services",
    community: "Woodlea community facilities · sports grounds",
  },

  internal: {
    sda_provider: "",
    property_owner: "Alpha (leased)",
    referral_source: "direct",
    landlord: "",
    management_notes: "Pre-launch — accepting EOIs.",
    contact: { name: "Intake team", email: "SIL@alphacommunitycare.com.au", phone: "1300 380 692" },
  },

  headline: "A brand-new SIL home, built for calm accessible living.",
  oneLiner:
    "A new-build Alpha SIL home in Melbourne's western growth corridor — practical, accessible ground-floor living with 1:1 or 1:2 support.",

  badges: [
    { label: "SIL House", tone: "purple" },
    { label: "1:1 & 1:2 support only", tone: "green" },
    { label: "Coming soon · Register interest", tone: "neutral" },
  ],

  specs: [
    { k: "Service", v: "Supported Independent Living" },
    { k: "Support ratio", v: "1:1 and 1:2 only" },
    { k: "Layout", v: "Two-storey · ground-floor living option" },
    { k: "Location", v: "Aintree, VIC" },
    { k: "Availability", v: "Pre-launch — register interest now" },
    { k: "Support hours", v: "24/7 on-call" },
  ],

  features: [
    "Step-free entry",
    "Wide internal doorways",
    "Ground-floor living option",
    "Accessible bathroom",
    "Timber-look flooring for calm acoustics",
    "Open-plan living & dining",
    "Full kitchen with island bench",
    "Double basin vanity",
    "Walk-in shower",
    "Main bedroom with built-in wardrobe",
    "Direct ensuite access from main bedroom",
    "Upstairs second living area",
    "Recessed LED lighting throughout",
    "Soft-tone carpets to bedrooms",
    "Covered alfresco patio",
    "Fully-fenced rear yard",
    "Off-street parking for two",
    "Close to community services",
  ],

  ndis: {
    summary:
      "Delivered under the NDIS as Supported Independent Living (SIL). Aintree suits participants who want a quieter, modern environment with consistent close support.",
    points: [
      "Services delivered as 1:1 or 1:2 support ratios only — never higher.",
      "Staff matched to each resident's preferences, culture and routines.",
      "Overnight and 24/7 on-call availability.",
      "Coordinated clinical oversight on request.",
      "Pre-launch — capacity is being prioritised for early enquiries.",
    ],
  },

  description: [
    "Set across two well-proportioned levels, the Aintree house is a brand-new build finished in a calm, modern palette — pale walls, timber-look flooring and stone-look benchtops.",
    "The downstairs open-plan living and dining flows out through a wide sliding door to a covered alfresco patio and a fully-fenced rear yard — bright, easy-care and made for everyday calm.",
    "The kitchen sits at the heart of the home with a generous island bench and quality appliances that make daily living practical and safe.",
    "Upstairs, a second living area gives residents extra room to move — quiet time away from the main level — with large windows and even lighting keeping the space calm.",
    "The main bedroom is generously sized and softly carpeted, opening directly to its own ensuite with a bath option, double-basin vanity and walk-in shower for accessible morning routines.",
    "Two full amenities are finished to a practical, dignified standard — level flooring, wide entries and grab-rail-ready walls — so 1:1 and 1:2 support runs without waiting.",
  ],

  // Real-estate-style quick facts row (accurate to visible photos only).
  quickFacts: [
    { Icon: "Layers", k: "Layout", v: "Two-storey" },
    { Icon: "Bath", k: "Bathrooms", v: "2 accessible" },
    { Icon: "Car", k: "Parking", v: "Double off-street" },
    { Icon: "Users", k: "Support ratio", v: "1:1 & 1:2 only" },
    { Icon: "Calendar", k: "Status", v: "Coming soon" },
  ],

  images: [
    { src: "/homes/aintree/1.jpg", alt: "Aintree front exterior — brand-new two-storey SIL home with double garage" },
    { src: "/homes/aintree/2.jpg", alt: "Accessible bathroom with double basin vanity and bath option" },
    { src: "/homes/aintree/3.jpg", alt: "Upstairs second living area with large windows and even lighting" },
    { src: "/homes/aintree/4.jpg", alt: "Ensuite with double vanity, bath and walk-in shower for supported morning routines" },
    { src: "/homes/aintree/5.jpg", alt: "Open-plan living, kitchen with island bench and sliding door to yard" },
    { src: "/homes/aintree/6.jpg", alt: "Rear exterior — covered alfresco patio and fully-fenced rear lawn" },
    { src: "/homes/aintree/7.jpg", alt: "Main bedroom with built-in wardrobe and direct access to ensuite" },
  ],

  story: {
    designedFor: {
      eyebrow: "Designed for calm",
      title: "A brand-new home, ready to live in.",
      body: "Aintree was built fresh — quiet palette, soft carpets, full sun and clean architectural lines. Move in without renovation, retrofit or compromise.",
      image: "/homes/aintree/5.jpg",
    },
    indoorOutdoor: {
      eyebrow: "Indoor & outdoor living",
      title: "From the kitchen to the lawn — one calm flow.",
      body: "Open-plan downstairs spills out to a covered alfresco patio and a fully-fenced rear yard. Easy to use, easy to live with.",
      image: "/homes/aintree/6.jpg",
    },
    selfCare: {
      eyebrow: "Accessible private suites",
      title: "A main bedroom that opens to its own ensuite.",
      body: "Soft carpet, built-in wardrobe and direct access to an ensuite with double-basin vanity and walk-in shower — supporting practical morning routines.",
      image: "/homes/aintree/7.jpg",
    },
  },

  accessibility: [
    { title: "Ground-floor living option", text: "Daily living space accessible without stairs on the ground floor." },
    { title: "Wide internal doorways", text: "Comfortable for mobility aids throughout." },
    { title: "Walk-in showers", text: "Level-entry glass showers — easy on every shift." },
    { title: "Double-vanity bathrooms", text: "Reduce wait times, support 1:1 + 1:2 routines." },
    { title: "Soft acoustics", text: "Carpet upstairs absorbs sound — quieter sensory load." },
    { title: "Outdoor flat lawn", text: "Fully fenced — suitable for assistive equipment and pets." },
  ],

  idealFor: [
    "Participants ready to start in a brand-new home",
    "People who benefit from low-stim modern interiors",
    "Adults who enjoy gardens, sun and outdoor time",
    "Households suited to 1:1 and 1:2 support delivery",
    "Anyone wanting a calm, well-considered accessible space",
  ],

  whyLove: [
    "It feels brand-new — because it is.",
    "The kitchen is the social heart.",
    "Bathrooms are set up for real daily routines.",
    "The yard makes it home — not a unit.",
  ],

  lifestyle: {
    headline: "Melbourne's western growth corridor.",
    blurb:
      "Aintree is part of a fresh, family-oriented neighbourhood with parks, schools and brand-new community amenity within minutes.",
    points: [
      { k: "Parks & open space", v: "Multiple new reserves walking distance" },
      { k: "Shopping", v: "Woodlea Town Centre nearby" },
      { k: "Healthcare", v: "GP, pharmacy + allied health a short drive" },
      { k: "Connection", v: "Direct access to the Western Freeway corridor" },
    ],
  },
};
